import {Grid} from '@mui/material';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {ExamModule, UserModule} from '@cp/model';
import useDeleteProgress from '../../hooks/userProgress/useDeleteProgress';
import useRemoveSelectedModules from '../../hooks/users/useRemoveSelectedModules';
import DeleteModuleWithProgressDialog from './DeleteModuleWithProgressDialog';
import CoursesSelectedModulesCard from './CoursesSelectedModulesCard';

export interface CoursesSelectedModulesProps {
  userModules: UserModule[];
  examModules: ExamModule[];
}

export default function CoursesSelectedModules({userModules, examModules}: CoursesSelectedModulesProps) {
  const navigate = useNavigate();
  const {removeSelectedModules, loading: deletingModule} = useRemoveSelectedModules();
  const {deleteProgress, loading: deletingProgress} = useDeleteProgress();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<{module: ExamModule; userModule: UserModule} | null>(null);

  const handleCardClick = (moduleId: string) => {
    navigate(`/course/${moduleId}`);
  };

  const handleDeleteClick = (moduleId: string) => {
    const userModule = userModules.find((m) => m.moduleId === moduleId);
    const examModule = examModules.find((m) => m.id === moduleId);
    if (userModule && examModule) {
      setModuleToDelete({module: examModule, userModule});
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async (shouldDeleteProgress: boolean) => {
    if (!moduleToDelete) return;

    try {
      if (shouldDeleteProgress) {
        await deleteProgress(moduleToDelete.module.id);
      }
      await removeSelectedModules(moduleToDelete.module.id);
      setDeleteDialogOpen(false);
      setModuleToDelete(null);
    } catch (err) {
      console.error('Error deleting module:', err);
      // Error handling could be improved with a toast notification
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setModuleToDelete(null);
  };

  const deleting = deletingModule || deletingProgress;

  return (
    <>
      <Grid container spacing={3}>
        {userModules
          .map((userModule) => ({
            userModule,
            examModule: examModules.find((m) => m.id === userModule.moduleId),
          }))
          .filter((pair): pair is {userModule: UserModule; examModule: ExamModule} => pair.examModule != null)
          .map(({userModule, examModule}) => (
            <Grid size={{xs: 12, sm: 6, md: 4}} key={userModule.moduleId}>
              <CoursesSelectedModulesCard
                userModule={userModule}
                examModule={examModule}
                onClick={handleCardClick}
                onDelete={handleDeleteClick}
                deleting={deleting}
              />
            </Grid>
          ))}
      </Grid>
      <DeleteModuleWithProgressDialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        module={moduleToDelete?.module || null}
        userModule={moduleToDelete?.userModule || null}
        loading={deleting}
      />
    </>
  );
}
